import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { FinxaiMktData } from '../models/finxaiMktData';
import logger from '../utils/logger';

interface OrderFormProps {
    data: FinxaiMktData;
    side: 'BUY' | 'SELL';
    onClose: () => void;
}

const OrderForm: React.FC<OrderFormProps> = ({ data, side, onClose }) => {
    const loggedInUser = useSelector((state: RootState) => state.user.loggedInUser);

    // Form state
    const [orderSide, setOrderSide] = useState<'BUY' | 'SELL'>(side);
    const [quantity, setQuantity] = useState<number>(100);
    const [price, setPrice] = useState<number>(side === 'BUY' ? data.offer_px : data.bid_px);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedSummary, setGeneratedSummary] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState<boolean>(false);

    const handleSideChange = (newSide: 'BUY' | 'SELL') => {
        setOrderSide(newSide);
        // Auto-update price based on side
        setPrice(newSide === 'BUY' ? data.offer_px : data.bid_px);
        // Clear previous summary when changing side
        setGeneratedSummary('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!loggedInUser) {
            alert('You must be logged in to generate order');
            return;
        }

        if (quantity <= 0) {
            alert('Quantity must be greater than 0');
            return;
        }

        setIsSubmitting(true);

        try {
            // Generate summary string
            const summary = `${data.security_id}: ${orderSide} ${quantity} @ ${price.toFixed(3)}`;
            setGeneratedSummary(summary);

            logger.debug(`Order summary generated: ${summary}`);
        } catch (error) {
            console.error('Error generating summary:', error);
            alert('Failed to generate order summary. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(generatedSummary);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            alert('Failed to copy to clipboard');
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
            }}
            onClick={handleOverlayClick}
        >
            <div
                style={{
                    backgroundColor: '#1e1e1e',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
                    width: '380px',
                    maxWidth: '90%',
                    padding: '16px',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h2
                            style={{
                                margin: 0,
                                color: orderSide === 'BUY' ? '#22c55e' : '#ef4444',
                                fontSize: '18px',
                                fontWeight: 'bold',
                            }}
                        >
                            Order
                        </h2>
                        {/* Side Toggle */}
                        <div style={{ display: 'flex', gap: '4px', backgroundColor: '#2d2d2d', padding: '2px', borderRadius: '4px', border: '1px solid #444' }}>
                            <button
                                type="button"
                                onClick={() => handleSideChange('BUY')}
                                style={{
                                    padding: '4px 12px',
                                    backgroundColor: orderSide === 'BUY' ? '#22c55e' : 'transparent',
                                    border: 'none',
                                    borderRadius: '3px',
                                    color: orderSide === 'BUY' ? '#fff' : '#999',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s',
                                }}
                            >
                                BUY
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSideChange('SELL')}
                                style={{
                                    padding: '4px 12px',
                                    backgroundColor: orderSide === 'SELL' ? '#ef4444' : 'transparent',
                                    border: 'none',
                                    borderRadius: '3px',
                                    color: orderSide === 'SELL' ? '#fff' : '#999',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s',
                                }}
                            >
                                SELL
                            </button>
                        </div>
                    </div>
                    <p style={{ margin: '4px 0 0', color: '#999', fontSize: '12px' }}>
                        {data.security_id}
                    </p>
                </div>

                {/* Market Data Info */}
                <div
                    style={{
                        backgroundColor: '#2d2d2d',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        fontSize: '12px',
                    }}
                >
                    <span style={{ color: '#999' }}>
                        Bid: <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{data.bid_px.toFixed(2)}</span>
                    </span>
                    <span style={{ color: '#666' }}>|</span>
                    <span style={{ color: '#999' }}>
                        Offer: <span style={{ color: '#ef4444', fontWeight: 'bold' }}>{data.offer_px.toFixed(2)}</span>
                    </span>
                </div>

                {/* Order Form */}
                <form onSubmit={handleSubmit}>
                    {/* Quantity */}
                    <div style={{ marginBottom: '10px' }}>
                        <label
                            htmlFor="quantity"
                            style={{ display: 'block', marginBottom: '4px', color: '#ddd', fontSize: '12px' }}
                        >
                            Quantity
                        </label>
                        <input
                            id="quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value))}
                            min="1"
                            step="1"
                            required
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                backgroundColor: '#2d2d2d',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                color: '#fff',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                            }}
                        />
                        {/* Quantity Shortcuts */}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                            {[5, 10, 20, 50, 100, 500].map((qty) => (
                                <button
                                    key={qty}
                                    type="button"
                                    onClick={() => setQuantity(qty)}
                                    style={{
                                        flex: '1 1 auto',
                                        minWidth: '48px',
                                        padding: '4px 8px',
                                        backgroundColor: quantity === qty ? '#3b82f6' : '#2d2d2d',
                                        border: '1px solid #444',
                                        borderRadius: '3px',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: quantity === qty ? 'bold' : 'normal',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (quantity !== qty) {
                                            e.currentTarget.style.backgroundColor = '#3a3a3a';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (quantity !== qty) {
                                            e.currentTarget.style.backgroundColor = '#2d2d2d';
                                        }
                                    }}
                                >
                                    {qty}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Price */}
                    <div style={{ marginBottom: '12px' }}>
                        <label
                            htmlFor="price"
                            style={{ display: 'block', marginBottom: '4px', color: '#ddd', fontSize: '12px' }}
                        >
                            Price
                        </label>
                        <input
                            id="price"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(Number(e.target.value))}
                            step="0.01"
                            required
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                backgroundColor: '#2d2d2d',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                color: '#fff',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: '#2d2d2d',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                color: '#fff',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: 'bold',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: orderSide === 'BUY' ? '#22c55e' : '#ef4444',
                                border: 'none',
                                borderRadius: '3px',
                                color: '#fff',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                opacity: isSubmitting ? 0.6 : 1,
                            }}
                        >
                            {isSubmitting ? 'Generating...' : 'Generate Order Text'}
                        </button>
                    </div>
                </form>

                {/* Generated Summary Section */}
                {generatedSummary && (
                    <div
                        style={{
                            marginTop: '12px',
                            padding: '10px',
                            backgroundColor: '#2d2d2d',
                            border: '1px solid #444',
                            borderRadius: '4px',
                        }}
                    >
                        <div style={{ fontSize: '10px', color: '#999', marginBottom: '6px', fontWeight: 'bold' }}>
                            Generated Order Summary
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    padding: '6px 8px',
                                    backgroundColor: '#1e1e1e',
                                    border: '1px solid #444',
                                    borderRadius: '3px',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {generatedSummary}
                            </div>
                            <button
                                type="button"
                                onClick={handleCopyToClipboard}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: copySuccess ? '#22c55e' : '#3b82f6',
                                    border: 'none',
                                    borderRadius: '3px',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s',
                                }}
                            >
                                {copySuccess ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderForm;

